import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import CustomSelect from 'components/Forms/CustomSelect';
import SubmitErrorMessage from 'components/Forms/SubmitErrorMessage';
import { getTrainersCompliant } from 'http/users';
import { getCompanyTrainingModuleTrainers, updateCompanyTrainingModuleTrainers } from 'http/trainingModules';
import { resolveApiErrorMessage, transformDataToSelectOptions } from 'utils';

const useStyles = makeStyles(theme => ({
  section: {
    maxWidth: 720,
  },
  description: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  actions: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const mapAssignedToOptions = items =>
  (items || []).map(item => ({
    value: item.userId,
    label: `${item.firstName} ${item.lastName}`.trim() || item.email,
  }));

const ModuleTrainersEditor = ({ trainingModuleId, readOnly = false }) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [initialTrainers, setInitialTrainers] = useState([]);

  const [loadState, loadData] = useAsyncFn(async () => {
    const [assignedResponse, trainersResponse] = await Promise.all([
      getCompanyTrainingModuleTrainers(trainingModuleId),
      getTrainersCompliant(),
    ]);

    if (assignedResponse?.error || (assignedResponse?.statusCode && assignedResponse.statusCode >= 400)) {
      return assignedResponse;
    }

    if (!trainersResponse.error) {
      setTrainerOptions(transformDataToSelectOptions(trainersResponse.items || [], 'id', 'fullName', true));
    }

    const assignedOptions = mapAssignedToOptions(assignedResponse.items);
    setInitialTrainers(assignedOptions);

    return assignedResponse;
  }, [trainingModuleId]);

  const [saveState, saveTrainers] = useAsyncFn(
    async values => {
      const trainerUserIds = Array.isArray(values.trainers) ? values.trainers.map(trainer => trainer.value) : [];

      const result = await updateCompanyTrainingModuleTrainers(trainingModuleId, {
        trainerUserIds,
      });

      if (!result?.error && !(result?.statusCode && result.statusCode >= 400)) {
        setInitialTrainers(mapAssignedToOptions(result.items));
      }

      return result;
    },
    [trainingModuleId],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const initialValues = useMemo(
    () => ({
      trainers: initialTrainers,
    }),
    [initialTrainers],
  );

  if (loadState.loading) {
    return null;
  }

  if (loadState.error || loadState.value?.error || (loadState.value?.statusCode && loadState.value.statusCode >= 400)) {
    return (
      <Typography color="error">
        {resolveApiErrorMessage(loadState.value?.message, {
          t,
          fallbackMessage: t('errors.serverError'),
        })}
      </Typography>
    );
  }

  return (
    <Box className={classes.section}>
      <Typography variant="body2" className={classes.description}>
        {t('trainingModules.trainers.description')}
      </Typography>

      <Form
        key={initialTrainers.map(trainer => trainer.value).join(',')}
        initialValues={initialValues}
        onSubmit={async values => {
          const result = await saveTrainers(values);
          if (result?.error || (result?.statusCode && result.statusCode >= 400)) {
            return {
              [FORM_ERROR]: resolveApiErrorMessage(result.message, {
                t,
                fallbackMessage: t('errors.serverError'),
              }),
            };
          }
          return undefined;
        }}
        render={({ handleSubmit, submitting, submitFailed, submitError }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Field
                  id="trainers"
                  name="trainers"
                  component={CustomSelect}
                  label={t('trainingModules.trainers.fieldLabel')}
                  placeholder={t('placeholders.chooseTrainer')}
                  options={trainerOptions}
                  isMulti
                  isDisabled={readOnly || submitting || saveState.loading}
                  fullWidth
                />
              </Grid>
            </Grid>

            {submitFailed && submitError ? <SubmitErrorMessage message={submitError} /> : null}

            {!readOnly ? (
              <Box className={classes.actions}>
                <Button type="submit" variant="contained" color="primary" disabled={submitting || saveState.loading}>
                  {t('buttons.save')}
                </Button>
              </Box>
            ) : null}
          </form>
        )}
      />
    </Box>
  );
};

ModuleTrainersEditor.propTypes = {
  trainingModuleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  readOnly: PropTypes.bool,
};

export default ModuleTrainersEditor;
